import { ReactElement, useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

import {
  LC_PAGE_SUBJECT_CHAR_LIMIT,
  LP_PAGE_REFERENCE_CHAR_LIMIT,
} from '@lessonPlanner/constants';
import { Language } from '@lessonPlanner/enums';

import {
  SelectField,
  TextAreaField,
  TextField,
} from '../components/form-fields';
import GenericBanner from '../components/GenericBanner';
import MainContent from '../components/MainContent';
import DynamicLabelGenerateButton from '../components/molecules/DynamicLabelGenerateButton';
import { GradeLevelDropdown } from '../components/Select';
import { PAGE_BUTTON_TEXT } from '../constants/buttonConstants';
import {
  CATCH_UP_FRIDAYS_DESCRIPTION,
  CATCH_UP_FRIDAYS_PAGE_TITLE,
} from '../constants/catchUpFridays';
import { defaultCreditCost } from '../constants/credits';
import { useGeneratePause } from '../context/generate/provider';
import { FeatureType } from '../enums/feature';
import { CatchUpFridaysProperties, PageKey } from '../enums/pageKey';
import useAutosizeTextArea from '../hooks/useAutoSizeTextArea';
import { useScrollToError } from '../hooks/useScrollToError';
import SidebarLayout from '../layouts/SidebarLayout';
import CatchUpFridaysSchema from '../schemas/CatchUpFridaysSchema';
import { catchUpFridaysProperties } from '../types/page';
import processInput from '../utils/formInput';
import { getLocalStorageItem } from '../utils/helper';

export default function CatchUpFridays(): JSX.Element {
  const defaultValues = {
    subject: getLocalStorageItem(CatchUpFridaysProperties.subject),
    gradeLevel: getLocalStorageItem(CatchUpFridaysProperties.gradeLevel),
    quarterlyTheme: getLocalStorageItem(
      CatchUpFridaysProperties.quarterlyTheme,
    ),
    subTheme: getLocalStorageItem(CatchUpFridaysProperties.subTheme),
    sessionObjectives: getLocalStorageItem(
      CatchUpFridaysProperties.sessionObjectives,
    ),
    language: getLocalStorageItem(CatchUpFridaysProperties.language, 'English'),
  };

  const catchUpFridaysForm = useForm({
    defaultValues,
    mode: 'onBlur',
    resolver: yupResolver(CatchUpFridaysSchema),
  });

  const {
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = catchUpFridaysForm;

  const subject = watch('subject');
  const gradeLevel = watch('gradeLevel');
  const language = watch('language');
  const quarterlyTheme = watch('quarterlyTheme');
  const subTheme = watch('subTheme');
  const sessionObjectives = watch('sessionObjectives');

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  useAutosizeTextArea(textAreaRef.current, quarterlyTheme);
  useAutosizeTextArea(textAreaRef.current, subTheme);
  useAutosizeTextArea(textAreaRef.current, sessionObjectives);

  const formContents = {
    subject,
    gradeLevel,
    quarterlyTheme,
    subTheme,
    sessionObjectives,
    language,
    feature: FeatureType.CATCH_UP_FRIDAYS,
  };

  async function triggerValidation(fieldName) {
    await trigger(fieldName);
  }

  async function updateFieldValue(fieldName, value) {
    setValue(fieldName, value);
  }

  async function customFormValidator() {
    await trigger();
  }

  const hasErrors = Object.entries(errors).length > 0;
  useScrollToError(errors);

  const { pausedButtonText, isGeneratePaused } = useGeneratePause();
  const buttonText = isGeneratePaused
    ? pausedButtonText
    : PAGE_BUTTON_TEXT[PageKey.catchUpFridays];

  const isGenerateButtonDisabled = isLoading || hasErrors || isGeneratePaused;

  const banner = (
    <GenericBanner
      content={CATCH_UP_FRIDAYS_DESCRIPTION}
      title={CATCH_UP_FRIDAYS_PAGE_TITLE}
    />
  );

  return (
    <MainContent
      banner={banner}
      formContents={formContents as catchUpFridaysProperties}
      pageKey={PageKey.catchUpFridays}
      setIsLoading={setIsLoading}
    >
      <>
        <TextField
          className="col-span-full"
          errorMessage={!!errors.subject && 'Please enter the Subject'}
          id="subject"
          isMaxLengthReached={subject?.length >= LC_PAGE_SUBJECT_CHAR_LIMIT}
          label="Subject"
          maxLength={LC_PAGE_SUBJECT_CHAR_LIMIT}
          name="subject"
          placeholder={'Input the subject for this lesson log'}
          type="subject"
          value={subject}
          onBlur={() => {
            processInput(
              updateFieldValue,
              triggerValidation,
              'subject',
              subject?.trim() || '',
            );
          }}
          onChange={(e) =>
            processInput(
              updateFieldValue,
              triggerValidation,
              'subject',
              e.target.value,
            )
          }
        />
        <GradeLevelDropdown
          error={!!errors.gradeLevel}
          errorMessage={!!errors.gradeLevel && 'Please select the Grade Level'}
          value={gradeLevel}
          onChange={(value) => updateFieldValue('gradeLevel', value)}
          onBlur={() => {
            triggerValidation('gradeLevel');
          }}
        />
        <TextAreaField
          className="col-span-full"
          error={!!errors.quarterlyTheme}
          id="quarterlyTheme"
          label="Quarterly Theme"
          link="https://www.deped.gov.ph/wp-content/uploads/DM_s2024_001.pdf#page=9"
          maxLength={LP_PAGE_REFERENCE_CHAR_LIMIT}
          name="quarterlyTheme"
          placeholder="Input the main theme here, refer to your teaching guide"
          rows={2}
          textAreaRef={textAreaRef}
          type="quarterlyTheme"
          value={quarterlyTheme}
          errorMessage={
            !!errors.quarterlyTheme && 'Please enter the quarterly theme'
          }
          isMaxLengthReached={
            quarterlyTheme?.length >= LP_PAGE_REFERENCE_CHAR_LIMIT
          }
          onBlur={() => {
            processInput(
              updateFieldValue,
              triggerValidation,
              'quarterlyTheme',
              quarterlyTheme?.trim() || '',
            );
          }}
          onChange={(e) =>
            processInput(
              updateFieldValue,
              triggerValidation,
              'quarterlyTheme',
              e.target.value,
            )
          }
        />

        <TextAreaField
          className="col-span-full"
          error={!!errors.subTheme}
          errorMessage={!!errors.subTheme && 'Please enter the sub-theme'}
          id="subTheme"
          isMaxLengthReached={subTheme?.length >= LP_PAGE_REFERENCE_CHAR_LIMIT}
          label="Sub-theme"
          maxLength={LP_PAGE_REFERENCE_CHAR_LIMIT}
          name="subTheme"
          placeholder="Input the sub-theme here, refer to your teaching guide"
          rows={2}
          textAreaRef={textAreaRef}
          type="subTheme"
          value={subTheme}
          onBlur={() => {
            processInput(
              updateFieldValue,
              triggerValidation,
              'subTheme',
              subTheme?.trim() || '',
            );
          }}
          onChange={(e) =>
            processInput(
              updateFieldValue,
              triggerValidation,
              'subTheme',
              e.target.value,
            )
          }
        />
        <TextAreaField
          className="col-span-full"
          error={!!errors.sessionObjectives}
          id="sessionObjectives"
          label="Session Objectives"
          maxLength={LP_PAGE_REFERENCE_CHAR_LIMIT}
          name="sessionObjectives"
          placeholder="Input the desired session objectives"
          rows={2}
          textAreaRef={textAreaRef}
          type="sessionObjectives"
          value={sessionObjectives}
          errorMessage={
            !!errors.sessionObjectives && 'Please enter the session objectives'
          }
          isMaxLengthReached={
            sessionObjectives?.length >= LP_PAGE_REFERENCE_CHAR_LIMIT
          }
          onBlur={() => {
            processInput(
              updateFieldValue,
              triggerValidation,
              'sessionObjectives',
              sessionObjectives?.trim() || '',
            );
          }}
          onChange={(e) =>
            processInput(
              updateFieldValue,
              triggerValidation,
              'sessionObjectives',
              e.target.value,
            )
          }
        />
        <SelectField
          className="col-span-full"
          error={!!errors.language}
          errorMessage={errors.language?.message}
          id="language"
          label="Translation"
          name="language"
          value={language}
          onChange={(value) => updateFieldValue('language', value)}
          onBlur={() => {
            triggerValidation('language');
          }}
        >
          <option value={Language.ENGLISH}>{Language.ENGLISH}</option>
          <option value={Language.FILIPINO}>{Language.FILIPINO}</option>
        </SelectField>
        <div className="col-span-full">
          <div className="flex flex-col">
            <DynamicLabelGenerateButton
              isRegularDefault
              buttonText={buttonText}
              creditCost={defaultCreditCost}
              handleOnCLick={customFormValidator}
              isDisabled={isGenerateButtonDisabled}
            />
          </div>
        </div>
      </>
    </MainContent>
  );
}

CatchUpFridays.getLayout = function getLayout(page: ReactElement) {
  return <SidebarLayout>{page}</SidebarLayout>;
};
